import { apiClient } from '@/lib/api/client'

/**
 * Auth API — HF OAuth (GDEV-177).
 *
 * Backend 엔드포인트:
 * - POST /auth/hf/login       → { authorize_url }
 * - POST /auth/hf/callback    → { user, hf_link_status }
 * - POST /auth/hf/disconnect  → UserProfile
 * - POST /auth/cli-token     → { access_token, expires_in }
 *
 * 쿠키 (HttpOnly):
 * - `hf_oauth_state`: /hf/login 이 설정. /hf/callback 이 검증 후 삭제. 10분 TTL.
 * - `access_token` / `refresh_token`: /hf/callback 성공 시 설정.
 *
 * 주의: `apiClient` 는 기본적으로 `credentials: 'include'` 로 쿠키를 동반한다.
 */

export interface UserProfile {
  id: string
  email: string | null
  display_name: string | null
  hf_username: string | null
  /** 소속 HF 조직 (예: etri-humanoidrobotsystems). */
  hf_organization: string | null
  role: string
  is_active: boolean
  registration_status: 'pending' | 'approved' | 'rejected'
  registration_review_reason: string | null
  created_at: string
  last_login_at: string | null
  hf_link_status: 'connected' | 'disconnected'
  hf_org_membership_status: 'none' | 'pending' | 'member'
}

export interface HfLoginResponse {
  authorize_url: string
}

export interface HfCallbackResponse {
  user: UserProfile
  hf_link_status: 'connected' | 'disconnected'
}

/**
 * HF OAuth authorize URL 을 받아서 현재 브라우저를 HF 로 이동시킨다.
 *
 * Backend 가 state / nonce / code_verifier 를 쿠키에 저장하므로, FE 는 단순히
 * authorize_url 로 navigation 만 수행하면 된다.
 */
export async function startHfLogin(): Promise<void> {
  const { authorize_url } = await apiClient.post<HfLoginResponse>('/auth/hf/login')
  window.location.assign(authorize_url)
}

/**
 * HF OAuth 콜백 페이지에서 code/state 를 받아 token 교환을 트리거한다.
 */
export async function completeHfLogin(args: {
  code: string
  state: string
}): Promise<HfCallbackResponse> {
  return apiClient.post<HfCallbackResponse>('/auth/hf/callback', args)
}

/**
 * 현재 사용자의 HF 연동을 해제한다. 토큰/조직 멤버십만 초기화되며 계정은 유지.
 */
export async function disconnectHf(): Promise<UserProfile> {
  return apiClient.post<UserProfile>('/auth/hf/disconnect')
}

export interface CliTokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
  /** access_token 유효 기간(초). 서버 설정에서 온다. */
  expires_in: number
  /** CLI 토큰(refresh) 최대 사용 기간(일) — CLI 가 그동안 스스로 갱신한다 (2026-10-23 이슈 #10). */
  max_age_days?: number | null
}

/**
 * CLI / 프로그래밍용 액세스 토큰을 발급한다 (POST /auth/cli-token, GDEV-62).
 *
 * 브라우저 세션 쿠키로 호출하고, 응답의 `access_token` 을 CLI 의
 * `etri-robotai-cli auth login --platform` 으로 등록한다(응답의 refresh_token, CLI 가 스스로
 * 갱신 — 2026-10-23 이슈 #10). 브라우저 세션과는
 * 독립된 토큰 체인이라 한쪽이 탈취돼도 다른 쪽은 영향받지 않는다.
 * 이 호출은 쿠키를 설정하지 않는다 — 토큰은 응답 body 로만 온다.
 */
export async function issueCliToken(): Promise<CliTokenResponse> {
  return apiClient.post<CliTokenResponse>('/auth/cli-token')
}
